import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store";
import NotificationsEmailFilter from "./components/NotificationsEmailFilter";
import { NotificationsEmailHeader } from "./components/NotificationsEmailHeader";
import { emailsListLimits } from "configs/limits";
import {
  deleteEmailTemplateItem,
  EmailTemplateItem,
  getEmailTemplateList,
  getEmailTemplateTypes,
  handlePaginate,
  handleSelectAll as handleSelectAllItems,
  handleSelect as handleSelectItem,
} from "store/emails/slice";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

export const NofiticationsEmailList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const [limit, setLimit] = useState<number>(emailsListLimits[0]);
  const [filterByType, setFilterByType] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<{
    [key in keyof Omit<EmailTemplateItem, "headerImageRef" | "content">]:
      | "asc"
      | "desc"
      | "none";
  }>({
    title: "none",
    isActive: "none",
    id: "none",
    key: "none",
    type: "none",
  });
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByKey, setFilterByKey] = useState<string>("");
  const [filterByTitle, setFilterByTitle] = useState<string>("");

  const emailsList = useAppSelector((store) => store.emails.list);
  const isEmailListFetched = useAppSelector(
    (store) => store.emails.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.emails.totalItems);
  const totalPages = useAppSelector((store) => store.emails.totalPages);
  const activePage = useAppSelector((store) => store.emails.activePage);
  const selectedItems = useAppSelector((store) => store.emails.selectedItems);
  const emailTypes = useAppSelector((store) => store.emails.types);
  const processingItemId = useAppSelector(
    (store) => store.emails.processItemId,
  );
  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'emails',
    isListFetched: isEmailListFetched
  });

  useEffect(() => {
    if (!isEmailListFetched) {
      dispatch(
        getEmailTemplateList({
          limit: limit,
          offset: activePage - 1,
          order,
          key: filterByKey,
          title: filterByTitle,
          type: filterByType.map((el) => el.value as string),
          isActive:
            filterByActive[0] && filterByActive[0].value !== 3
              ? (filterByActive[0].value as number)
              : undefined,
        }),
      );
    }
  }, [activePage, limit, isEmailListFetched]);

  useEffect(() => {
    dispatch(getEmailTemplateTypes());
  }, [dispatch]);

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  function updateSort<T>(sort: T, key: keyof T) {
    const currentValueByKey = sort[key] as string;

    switch (currentValueByKey) {
      case "none": {
        return {
          ...sort,
          [key]: "asc",
        };
      }
      case "asc": {
        return {
          ...sort,
          [key]: "desc",
        };
      }

      case "desc": {
        return {
          ...sort,
          [key]: "none",
        };
      }

      default: {
        return {
          ...sort,
        };
      }
    }
  }

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(handlePaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(handleSelectAllItems());
  };

  const handleSelect = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handleApplyFilter = () => {
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!permissions?.delete) return;

    const result = await dispatch(deleteEmailTemplateItem(id));

    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOpenCreateModal = () => {
    if (!permissions?.create) return;

    navigate(`${location.pathname}?activeModal=${ModalTypes.email}&type=email`);
    dispatch(openModal({ activeModal: ModalTypes.email }));
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.email}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.email }));
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <NotificationsEmailFilter
        handleApply={handleApplyFilter}
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        onFilterByType={(v) =>
          setFilterByType(v as { value: string | number; label: string }[])
        }
        onFilterByActive={(v) =>
          setFilterByActive(v as { value: string | number; label: string }[])
        }
        onFilterByKey={setFilterByKey}
        filterByType={filterByType}
        filterByActive={filterByActive}
        filterByKey={filterByKey}
        types={emailTypes}
      />
      <div className="page-list--with-filter-content">
        <NotificationsEmailHeader
          options={options}
          createModal={() => handleOpenCreateModal()}
          isDisabled
          isCreateAllowed={permissions?.create}
          isRefreshRequested={isRefreshRequested}
          refresh={refresh}
        />

        {!isEmailListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={3} />
            <PaginationSkeleton />
          </>
        )}

        {isEmailListFetched && (
          <div className="game-table-wrap">
            {emailsList.length === 0 && (
              <EmptyState message={t("noAutoEmailsFound")} />
            )}

            {emailsList.length > 0 && (
              <Table
                data={emailsList}
                idField="id"
                order={order}
                onSort={(v) => {
                  const k = v as unknown;
                  handleOrderClick(k as keyof typeof order);
                }}
                selectedItems={selectedItems}
                hasCheckbox={permissions?.update ?? false}
                onAllSelect={handleSelectAll}
                onSelect={handleSelect}
                onDelete={handleDeleteItem}
                processingId={processingItemId}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            )}
          </div>
        )}

        {isEmailListFetched && emailsList.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={emailsListLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default NofiticationsEmailList;
